import { fetchConfig, resetShimmer } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Inline size swap for a cart line (design 1i).
 *
 * A click on a chip swaps the line to that variant in two Cart API calls: add the new variant with
 * the line's quantity, properties and selling plan, then set the old line to 0 while asking for the
 * cart sections. Adding first means a sold-out size fails before anything is removed. The chip state
 * changes at once (optimistic) and rolls back on error, where the line shows the API's message.
 *
 * The component dispatches a `CartLinesUpdateEvent` with a promise, the same contract Horizon's
 * quantity selector uses, so `cart-items-component` morphs the drawer from the response and
 * `cart-icon` updates the count. Progressive enhancement: without this module the chips are inert
 * and the line still shows its size in the options text.
 */

/** @typedef {{ userFacing?: boolean }} SwapErrorFlags */

/**
 * Turns a Cart API error body into an Error. The API answers 422 as `{ status, message, description }`
 * and some routes as `{ errors }`, where `errors` can be a string, an array or a keyed object.
 * @param {any} json
 * @param {string} fallback
 * @returns {Error & SwapErrorFlags}
 */
export function cartApiError(json, fallback) {
  const { errors } = json ?? {};
  const fromErrors =
    typeof errors === 'string'
      ? errors
      : Array.isArray(errors)
        ? errors.join(' ')
        : errors && typeof errors === 'object'
          ? Object.values(errors).flat().join(' ')
          : '';
  const description = json?.description || fromErrors;

  return Object.assign(new Error(description || json?.message || fallback), { userFacing: Boolean(description) });
}

/** @param {Response} response @param {any} json */
export const cartApiFailed = (response, json) => !response.ok || Boolean(json?.status) || Boolean(json?.errors);

class CartSizeSwap extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null;
    if (!(chip instanceof HTMLButtonElement) || !this.contains(chip)) return;
    if (chip.getAttribute('aria-pressed') === 'true' || chip.getAttribute('aria-disabled') === 'true') return;
    if (this.#busy) return;

    this.swap(chip);
  };

  get #cartItems() {
    return this.closest('cart-items-component');
  }

  /** @returns {Record<string, string>} */
  get #properties() {
    try {
      // Liquid renders a line with no properties as `[]`, which /cart/add.js rejects ("expected Array to be a Hash").
      const parsed = JSON.parse(this.dataset.properties || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  /** @param {string | null | undefined} message */
  #showError(message) {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message ?? '';
    error.hidden = !message;
  }

  /**
   * Waits a frame like #refocus: the drawer morph lands after the swap resolves and resets the
   * live region to its server-rendered (empty) text, which would cut the announcement.
   * @param {string | undefined} text
   */
  #announce(text) {
    if (!text) return;
    requestAnimationFrame(() =>
      setTimeout(() => {
        const region = document.getElementById('cart-drawer-live');
        if (region) region.textContent = text;
      })
    );
  }

  /**
   * After the morph the chips are new nodes; put focus back on the chosen size so keyboard users
   * stay on the line they were editing.
   * @param {string} variantId
   */
  #refocus(variantId) {
    requestAnimationFrame(() =>
      setTimeout(() => {
        const chip = document.querySelector(`cart-size-swap [data-variant-id="${variantId}"][aria-pressed="true"]`);
        if (chip instanceof HTMLElement) chip.focus({ preventScroll: true });
      })
    );
  }

  /** @param {HTMLButtonElement} chip */
  async swap(chip) {
    const { variantId } = chip.dataset;
    const key = this.dataset.key;
    const quantity = Number(this.dataset.quantity) || 1;
    if (!variantId || !key) return;

    const previous = this.querySelector('button[aria-pressed="true"]');
    const cartItems = this.#cartItems;
    const price = /** @type {any} */ (this.closest('tr')?.querySelector('text-component'));
    const hadFocus = this.contains(document.activeElement);

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.#showError(null);
    previous?.setAttribute('aria-pressed', 'false');
    chip.setAttribute('aria-pressed', 'true');
    cartItems?.classList.add('cart-items-disabled');
    price?.shimmer?.();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );

    let added = false;

    try {
      /** @type {Record<string, unknown>} */
      const addBody = { id: Number(variantId), quantity, properties: this.#properties };
      if (this.dataset.sellingPlan) addBody.selling_plan = Number(this.dataset.sellingPlan);

      const addResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(addBody) }));
      const addJson = await addResponse.json();
      if (cartApiFailed(addResponse, addJson)) throw cartApiError(addJson, 'Cart add failed');
      added = true;

      const changeResponse = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: key,
            quantity: 0,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const cart = await changeResponse.json();
      if (cartApiFailed(changeResponse, cart)) throw cartApiError(cart, 'Cart change failed');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-size-swap',
          didError: false,
        },
      });

      this.#announce(this.dataset.textChanged?.replace('[size]', chip.dataset.size ?? ''));
      if (hadFocus) this.#refocus(variantId);
    } catch (error) {
      const flags = /** @type {SwapErrorFlags} */ (error ?? {});
      const message = flags.userFacing && error instanceof Error ? error.message : this.dataset.textError || '';

      chip.setAttribute('aria-pressed', 'false');
      previous?.setAttribute('aria-pressed', 'true');
      this.#showError(message);
      if (cartItems) resetShimmer(cartItems);

      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: message || 'Cart update failed', code: 'INVALID' }));

      // The new variant is in the cart but the old line is still there: refetch so the drawer
      // shows what the cart really holds instead of the optimistic state.
      if (added && cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionRenderer.renderSection(cartItems.dataset.sectionId, { cache: false, mode: 'hydration' });
      }
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      cartItems?.classList.remove('cart-items-disabled');
    }
  }
}

if (!customElements.get('cart-size-swap')) {
  customElements.define('cart-size-swap', CartSizeSwap);
}
